import { PapiTag } from '@wix/da-papi-types';
import type { FC } from 'react';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url, navigateToSanitizedUrl } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  BiData,
  ClickCreateAiVideoBiEvent,
  DreamupInfoClickBiEvent,
  EditWithMuroBiEvent,
} from '@wix/da-bi/pkg/events';
import DreamUpStarsIcon from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import DreamUpStarsIcon16 from '@wix/da-ds/pkg/Icons/16x16/DreamUpStars';
import GhostButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/GhostButtonWithContextMenu';
import EmptyResult from '../../../EmptyResult';
import OrderDropdown from '../../_partials/OrderDropdown';
import BasePageLayout from '../../_partials/BasePageLayout';
import TagSliderSubnav from '../../_partials/TagSliderSubnav';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import { DailyChallengeArray } from './DailyChallenges/types';
import DailyChallengeTagPage from './DailyChallenges/DailyChallengeTagPage';
import { usePushMiniMobileMuroModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushMuroModal';

import s from './TagPage.scss';

export interface Props {
  tag?: PapiTag;
  hasOrderSubnav?: boolean;
  dailyChallenges?: DailyChallengeArray;
  pushMiniMuroModal: () => void;
}

const TagPage: FC<Props> = ({ tag, hasOrderSubnav, dailyChallenges, pushMiniMuroModal }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const isDesktop = !isMobile;
  const { isLoggedIn, sendUserToJoinPage } = useRestrictToLoggedIn();

  const pushMobileMuroModal = usePushMiniMobileMuroModal();

  const showMuroIfLoggedIn = useCallback(() => {
    if (isLoggedIn) {
      if (isMobile) {
        pushMobileMuroModal();
      } else {
        pushMiniMuroModal();
      }
    } else {
      sendUserToJoinPage(ReferralType.MURO_TOP_CREATE);
    }
  }, [isLoggedIn, isMobile, sendUserToJoinPage, pushMiniMuroModal, pushMobileMuroModal]);
  const showVideoCreatorIfLoggedIn = useCallback(
    (prompt?: string) => {
      if (isLoggedIn) {
        navigateToSanitizedUrl(Url.videoCreatorLink({ prompt }));
      } else {
        sendUserToJoinPage(ReferralType.VIDEO_CREATOR_TAG_PAGE);
      }
    },
    [isLoggedIn, sendUserToJoinPage]
  );

  if (!tag) {
    return <EmptyResult />;
  }

  // precondition: backend won't send dailyChallenges unless we've matched a tag in them
  const showDailyChallenges = !!dailyChallenges;
  if (showDailyChallenges) {
    return <DailyChallengeTagPage tag={tag} dailyChallenges={dailyChallenges} />;
  }

  const dreamUpBiData = BiData<DreamupInfoClickBiEvent>({
    evid: 923,
    typeid: ResourceType.DISCOVERYTAG,
    itemid: tag.itemId,
    sectionname: 'tag_header',
  });

  const createMenuItems = [
    {
      label: t('tagPage.headline.dreamupCta.label'),
      link: Url.dreamupLink({ prompt: tag.tag }),
      biData: dreamUpBiData,
    },
    {
      label: t('tagPage.headline.videoCta.label'),
      onClick: () => showVideoCreatorIfLoggedIn(tag.tag),
      biData: BiData<ClickCreateAiVideoBiEvent>({
        evid: 952,
        typeid: ResourceType.DISCOVERYTAG,
        itemid: tag.itemId,
        sectionname: 'tag_header',
      }),
    },
    {
      label: t('tagPage.headline.drawCta.label'),
      onClick: showMuroIfLoggedIn,
      biData: BiData<EditWithMuroBiEvent>({
        evid: 926,
        page_typeid: ResourceType.DISCOVERYTAG,
        page_itemid: tag.itemId,
        sectionname: 'tag_header',
      }),
    },
  ];

  const createMenuButtonMedium = (
    <GhostButtonWithContextMenu
      variant="main"
      size="medium"
      icon={DreamUpStarsIcon16}
      className={s['dreamup-cta-button']}
      items={createMenuItems}
    >
      {t('tagPage.headline.createMenu.label')}
    </GhostButtonWithContextMenu>
  );

  const headerLeftSide = isMobile ? (
    <h1 className={s['title']}>{tag.name}</h1>
  ) : (
    <div className={s['headline-with-cta']}>
      <h1 className={s['title']}>
        {t('tagPage.headline', {
          postProcess: ['reactPostprocessor', 'featureBrancher'],
          tag: <span className={s['title-tag']}>{tag.name}</span>,
        })}
      </h1>
      {createMenuButtonMedium}
    </div>
  );

  const tagSliderSubnav = (
    <TagSliderSubnav className={s['tag-slider-subnav']} withClearButton withTodaysTagIcon />
  );

  return (
    <BasePageLayout
      wrapperClassName={s['root']}
      aboveHeader={isMobile && tagSliderSubnav}
      headerClassName={s['header']}
      innerHeaderClassName={s['inner-header']}
      headerIsStickyOnScrollUp={false}
      headerLeftSideContent={headerLeftSide}
      headerRightSideContent={hasOrderSubnav && <OrderDropdown className={s['order-dropdown']} />}
      withNetworkBar={false}
      withNavigation={false}
      belowHeader={isDesktop && tagSliderSubnav}
      contentClassName={s['content-block']}
    >
      {isMobile && (
        <GhostButtonWithContextMenu
          variant="main"
          size="large"
          className={s['mobile-cta-dropdown']}
          standardMenuProps={{ className: s['dropdown-menu'] }}
          icon={DreamUpStarsIcon}
          items={[
            {
              label: t('tagPage.dailyChallenge.createWithDreamup.text'),
              link: Url.dreamupLink({ prompt: tag.tag }),
              biData: BiData<DreamupInfoClickBiEvent>({
                evid: 923,
                typeid: ResourceType.DISCOVERYTAG,
                itemid: tag.itemId,
                sectionname: 'daily_challenge_header',
              }),
            },
            {
              label: t('tagPage.headline.videoCta.label'),
              onClick: () => showVideoCreatorIfLoggedIn(tag.tag),
              biData: BiData<ClickCreateAiVideoBiEvent>({
                evid: 952,
                typeid: ResourceType.DISCOVERYTAG,
                itemid: tag.itemId,
                sectionname: 'tag_header',
              }),
            },
            {
              label: t('tagPage.dailyChallenge.createWithDraw.text'),
              onClick: showMuroIfLoggedIn,
              biData: BiData<EditWithMuroBiEvent>({
                evid: 926,
                page_typeid: ResourceType.DISCOVERYTAG,
                page_itemid: tag.itemId,
                sectionname: 'daily_challenge_header',
              }),
            },
          ]}
        >
          {t('tagPage.dailyChallenge.createMenuButton.text')}
        </GhostButtonWithContextMenu>
      )}

      <DefaultPageContent />
    </BasePageLayout>
  );
};
TagPage.displayName = 'TagPage';

export default TagPage;
